/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { DRAG_DROP_PASTE } from "@lexical/rich-text";
import { $insertNodeToNearestRoot, isMimeType, mediaFileReader, mergeRegister } from "@lexical/utils";
import { $getNodeByKey, $getRoot, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, DROP_COMMAND, PASTE_COMMAND } from "lexical";
import { useEffect } from "react";

import useEditorStore from "store/useEditorStore";
import { registerClip } from "redux/actions/vdocsActions";
import { useAppDispatch } from "hooks";
import { uploadFileImage } from "utils/aws/s3Interface";
import * as Sentry from "@sentry/react";
import { $createImageNode, $isImageNode, ImagePayload } from "components/NewEditor/nodes/SlidImageNode/ImageNode";
import { INSERT_IMAGE_COMMAND } from "../ImagesPlugin";

const ACCEPTABLE_IMAGE_TYPES = ["image/", "image/heic", "image/heif", "image/gif", "image/webp"];

export default function DragDropPaste(): null {
  const [editor] = useLexicalComposerContext();
  const { currentDocumentKey } = useEditorStore();
  const dispatch = useAppDispatch();

  const handleFilePaste = async (file: File) => {
    const handleImagePaste = async (file): Promise<ImagePayload | null> => {
      const uploadedImageSrc = await uploadFileImage({
        path: `image_upload/${currentDocumentKey}`,
        file: file,
      });

      if (!uploadedImageSrc) {
        Sentry.withScope((scope) => {
          scope.setLevel("error");
          scope.setExtra("message", "s3 upload failed");
          Sentry.captureMessage("SLID_WEB_ADD_IMAGE_BY_PASTE_ERROR");
        });
        return null;
      }

      const clipRegisterResponse = await dispatch(
        registerClip({
          imgSrc: uploadedImageSrc,
          documentKey: currentDocumentKey,
          clipTimestamp: null,
          videoKey: null,
          clipEndPoint: null,
        })
      );

      if (clipRegisterResponse.error_message) return null;
      return {
        clipKey: clipRegisterResponse["clip_key"],
        src: uploadedImageSrc,
        originalImageSrc: uploadedImageSrc,
        isUploaded: true,
        sourceType: "filePaste",
        documentKey: currentDocumentKey,
      };
    };

    const imageData = await handleImagePaste(file);

    if (imageData) {
      editor.dispatchCommand(INSERT_IMAGE_COMMAND, imageData);
    }
  };

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand(
        DRAG_DROP_PASTE,
        (files) => {
          (async () => {
            const filesResult = await mediaFileReader(
              files,
              [ACCEPTABLE_IMAGE_TYPES].flatMap((x) => x)
            );
            for (const { file, result } of filesResult) {
              let nodeKey: string = "";
              editor.update(() => {
                const imageNode = $createImageNode({
                  src: result,
                  sourceType: "filePaste",
                  clipKey: "",
                  originalImageSrc: result,
                  isUploaded: false,
                });
                $insertNodeToNearestRoot(imageNode);

                nodeKey = imageNode.getKey();
              });
              const uploadedImageSrc = await uploadFileImage({
                path: `image_upload/${currentDocumentKey}`,
                file: file,
              });

              editor.update(() => {
                const imageNode = $getNodeByKey(nodeKey);
                if (imageNode && $isImageNode(imageNode)) {
                  imageNode.updateData({
                    isUploaded: true,
                    src: uploadedImageSrc,
                  });
                }
              });

              if (!uploadedImageSrc) {
                Sentry.withScope((scope) => {
                  scope.setLevel("error");
                  scope.setExtra("message", "s3 upload failed");
                  Sentry.captureMessage("SLID_WEB_ADD_IMAGE_BY_PASTE_ERROR");
                });
                return;
              }

              const clipRegisterResponse = await dispatch(
                registerClip({
                  imgSrc: uploadedImageSrc,
                  documentKey: currentDocumentKey,
                  clipTimestamp: null,
                  videoKey: null,
                  clipEndPoint: null,
                })
              );

              if (clipRegisterResponse.error_message) return;
              if (isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) {
                if (!!nodeKey) {
                  editor.update(() => {
                    const imageNode = $getNodeByKey(nodeKey);
                    if (imageNode && $isImageNode(imageNode)) {
                      imageNode.updateData({
                        isLoading: false,
                        src: uploadedImageSrc,
                        clipKey: clipRegisterResponse["clip_key"],
                        originalImageSrc: uploadedImageSrc,
                        isUploaded: true,
                      });
                    }
                  });
                }
              }
            }
          })();
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (event: ClipboardEvent) => {
          const clipboardData = event.clipboardData;

          if (!clipboardData) return false;
          const lexicalData = clipboardData.getData("application/x-lexical-editor");
          if (lexicalData) {
            const parsedData = JSON.parse(lexicalData);
            if (parsedData.nodes?.[0]?.type === "image") {
              event.preventDefault();
              editor.update(() => {
                const imageNode = $createImageNode(parsedData.nodes[0]);
                $getRoot().append(imageNode);
              });
              return true;
            }
          }
          const items = clipboardData.items;
          for (const item of items) {
            switch (item.kind) {
              case "string":
              case "file": {
                const file = item.getAsFile();
                if (file) {
                  handleFilePaste(file);
                  return true;
                }
                break;
              }

              // TODO: add pattern, tag case if needed
              default: {
                return false;
              }
            }
          }

          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
    return () => {
      unregister();
    };
  }, [editor]);
  return null;
}
